/**
 * Transport, over the whole book rather than the chapter.
 *
 * The scrubber is a native range input, kept invisible on top of a drawn track.
 * That buys the keyboard and screen reader behaviour for free, which a div with
 * pointer handlers would have had to reimplement badly, and still leaves the
 * track free to show buffered ranges under the played part.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BackIcon, ForwardIcon, PauseIcon, PlayIcon } from "../components/icons";
import { IconButton, clock } from "../components/ui";
import { t, useUiLang } from "../i18n";
import { Player } from "../lib/player";
import type { PlayerState } from "../lib/player";
import type { SleepPick, SleepState } from "../lib/sleepTimer";
import { AudioPrefsMenu, type AudioPrefsMenuValues } from "./AudioPrefsMenu";
import { SleepMenu } from "./SleepMenu";

/** Two glyphs the shared icon module does not carry, drawn on its 20px grid. */
function localIcon(path: React.ReactNode) {
  return function Icon({ className = "h-5 w-5" }: { className?: string }) {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
      >
        {path}
      </svg>
    );
  };
}

const BookmarkIcon = localIcon(<path d="M6 3.5h8v13l-4-3.2-4 3.2v-13z" />);
const MoonIcon = localIcon(<path d="M16 12.2A6.2 6.2 0 017.8 4a6.5 6.5 0 108.2 8.2z" />);

/**
 * Buffered ranges, read straight off the audio element.
 *
 * lib/player.ts owns that element, does not expose it, and never puts it in the
 * document, so there is no other way to reach it. This is a read only peek past
 * a TypeScript `private`, which is a compile time fiction: the day the field is
 * renamed this returns null and the bar quietly stops drawing the buffer.
 */
function bufferedOf(player: Player): [number, number][] {
  const inside = player as unknown as { audio?: unknown };
  const audio = inside.audio;
  if (!(audio instanceof HTMLAudioElement)) return [];
  const ranges: [number, number][] = [];
  for (let at = 0; at < audio.buffered.length; at += 1) {
    ranges.push([audio.buffered.start(at), audio.buffered.end(at)]);
  }
  return ranges;
}

export function PlayerBar({
  player,
  state,
  hasSync,
  chapterIndex,
  chapterCount,
  onOpenChapters,
  onOpenBookmarks,
  sleep,
  audioPrefs,
}: {
  player: Player;
  state: PlayerState;
  hasSync: boolean;
  chapterIndex: number;
  chapterCount: number;
  onOpenChapters: () => void;
  onOpenBookmarks: () => void;
  sleep: {
    state: SleepState;
    pick: (choice: SleepPick) => void;
    extend: () => void;
    stop: () => void;
  };
  audioPrefs: {
    values: AudioPrefsMenuValues;
    onChange: (patch: Partial<AudioPrefsMenuValues>) => void;
  };
}) {
  useUiLang();
  // One name for the three surfaces the bar can open, so opening any of them
  // closes the other two: two popovers stacked over a 390px bar is unreadable.
  const [open, setOpen] = useState<"sleep" | "prefs" | null>(null);
  const [buffered, setBuffered] = useState<[number, number][]>([]);
  const [dragging, setDragging] = useState<number | null>(null);
  const pending = useRef(0);

  useEffect(() => {
    const sample = () => setBuffered(bufferedOf(player));
    sample();
    const timer = window.setInterval(sample, 700);
    return () => window.clearInterval(timer);
  }, [player]);

  const sleepOn = sleep.state.mode.kind !== "off";
  const sleepLeft = sleepOn ? sleep.state.remaining : null;

  const duration = state.duration || 0;
  const time = dragging ?? state.time;
  const percent = duration > 0 ? (time / duration) * 100 : 0;

  // Seeking on every input event of a drag would thrash a ranged stream. One seek
  // per frame is still live enough that the highlight follows the thumb.
  const scrub = useCallback(
    (value: number) => {
      setDragging(value);
      if (pending.current) return;
      pending.current = requestAnimationFrame(() => {
        pending.current = 0;
        player.seek(value);
      });
    },
    [player],
  );

  const bars = useMemo(
    () =>
      duration > 0
        ? buffered.map(([from, to]) => ({
            left: (from / duration) * 100,
            width: Math.max(0, ((to - from) / duration) * 100),
          }))
        : [],
    [buffered, duration],
  );

  return (
    // `env(safe-area-inset-bottom)` is the home indicator on an iPhone. Without
    // it the transport sits under the bar the system draws over everything, and
    // the play button is the thing the thumb misses.
    <footer className="shrink-0 bg-[var(--color-page)] px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] short:pt-1">
      <div className="mx-auto flex max-w-3xl flex-col gap-2 short:gap-1">
        <div className="flex items-center gap-3">
          <span className="w-14 shrink-0 text-right font-mono text-2xs tabular-nums text-[var(--color-faint)]">
            {clock(time)}
          </span>

          {/* A 4px track is unusable with a thumb, so the whole 44px band is
              the control: the drawn bar stays thin, the input covers the band. */}
          <div className="group relative h-6 flex-1 coarse:h-11">
            <div className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 overflow-hidden rounded-full bg-[var(--color-line)]">
              {bars.map((bar) => (
                <span
                  key={`${bar.left}-${bar.width}`}
                  className="absolute inset-y-0 bg-[var(--color-faint)]/40"
                  style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                />
              ))}
              <span
                className="absolute inset-y-0 left-0 bg-[var(--color-accent)]"
                style={{ width: `${percent}%` }}
              />
            </div>
            <span
              className={
                "pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 " +
                "rounded-full bg-[var(--color-accent)] opacity-0 transition-opacity duration-150 " +
                "group-hover:opacity-100 group-focus-within:opacity-100 coarse:h-4 coarse:w-4 coarse:opacity-100"
              }
              style={{ left: `${percent}%` }}
            />
            <input
              type="range"
              min={0}
              max={Math.max(1, duration)}
              step={1}
              value={time}
              disabled={duration === 0}
              aria-label={t("playerPosition")}
              aria-valuetext={t("playerPositionValue", {
                time: clock(time),
                total: clock(duration),
              })}
              onChange={(event) => scrub(Number(event.target.value))}
              onPointerUp={() => setDragging(null)}
              onPointerCancel={() => setDragging(null)}
              onTouchEnd={() => setDragging(null)}
              onBlur={() => setDragging(null)}
              // `touch-none` hands every gesture inside the band to the slider.
              // Without it the browser reads a slightly diagonal drag as a scroll
              // and the thumb stops following the finger halfway through.
              className="absolute inset-0 h-full w-full cursor-pointer touch-none appearance-none bg-transparent opacity-0 disabled:cursor-default"
            />
          </div>

          <span className="w-14 shrink-0 font-mono text-2xs tabular-nums text-[var(--color-faint)]">
            -{clock(Math.max(0, duration - time))}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              onClick={onOpenChapters}
              className="tap truncate rounded-lg px-2 py-1 text-xs text-[var(--color-muted)] transition-colors duration-150 hover:bg-[var(--color-raised)] hover:text-[var(--color-ink)]"
            >
              {/* Under 640px the word "Chapitre" costs more room than the two
                  numbers it introduces, and the whole label truncated to
                  "Chapitre ..." - which says nothing. Below that width the
                  numbers alone stay, and they always fit. */}
              <span className="hidden sm:inline">
                {t("readerChapter", { n: chapterIndex + 1 })}
                <span className="text-[var(--color-faint)]">
                  {" "}
                  {t("readerChapterOf", { total: chapterCount || 1 })}
                </span>
              </span>
              <span className="font-mono tabular-nums sm:hidden">
                {chapterIndex + 1}
                <span className="text-[var(--color-faint)]">/{chapterCount || 1}</span>
              </span>
            </button>
            {!hasSync && (
              <span
                title={t("playerNoSyncHint")}
                className="hidden shrink-0 rounded-full bg-[var(--color-raised)] px-2 py-0.5 text-2xs text-[var(--color-faint)] sm:inline"
              >
                {t("playerNoSync")}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            <IconButton
              label={t("playerSkipBack", { seconds: player.skipBackSeconds })}
              onClick={() => player.nudge(-player.skipBackSeconds)}
            >
              <BackIcon />
            </IconButton>
            <button
              onClick={() => player.toggle()}
              aria-label={state.playing ? t("playerPause") : t("playerPlay")}
              className={
                "flex h-11 w-11 items-center justify-center rounded-full bg-[var(--color-accent)] " +
                "coarse:h-14 coarse:w-14 short:coarse:h-12 short:coarse:w-12 " +
                "text-[var(--color-on-accent)] transition-transform duration-150 hover:brightness-110 active:scale-95"
              }
            >
              {state.playing ? <PauseIcon /> : <PlayIcon />}
            </button>
            <IconButton
              label={t("playerSkipForward", { seconds: player.skipForwardSeconds })}
              onClick={() => player.nudge(player.skipForwardSeconds)}
            >
              <ForwardIcon />
            </IconButton>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-0.5">
            {/* The error hides under 640px: at 390 the three controls need the
                room more than a message the transport already makes obvious. */}
            {state.error && (
              <span className="hidden truncate text-2xs text-[var(--color-faint)] sm:inline">
                {state.error}
              </span>
            )}

            <IconButton
              label={t("bookmarkOpen")}
              onClick={() => {
                setOpen(null);
                onOpenBookmarks();
              }}
            >
              <BookmarkIcon />
            </IconButton>

            <div className="relative">
              <IconButton
                label={
                  sleepLeft === null ? t("sleepOpen") : t("sleepRemaining", { time: clock(sleepLeft) })
                }
                aria-haspopup="menu"
                aria-expanded={open === "sleep"}
                onClick={() => setOpen(open === "sleep" ? null : "sleep")}
                className={sleepOn ? "text-[var(--color-accent)]" : undefined}
              >
                <MoonIcon />
              </IconButton>
              <SleepMenu
                open={open === "sleep"}
                onClose={() => setOpen(null)}
                state={sleep.state}
                onPick={sleep.pick}
                onExtend={sleep.extend}
              />
            </div>

            <div className="relative">
              <button
                onClick={() => setOpen(open === "prefs" ? null : "prefs")}
                aria-haspopup="menu"
                aria-expanded={open === "prefs"}
                aria-label={t("audioPrefsOpen", { rate: state.rate })}
                className={
                  "tap min-w-fib55 rounded-lg px-2 py-1.5 font-mono text-xs transition-colors duration-150 " +
                  (open === "prefs"
                    ? "bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
                    : "text-[var(--color-muted)] hover:bg-[var(--color-raised)] hover:text-[var(--color-ink)]")
                }
              >
                {state.rate}x
              </button>
              <AudioPrefsMenu
                open={open === "prefs"}
                onClose={() => setOpen(null)}
                prefs={audioPrefs.values}
                onChange={audioPrefs.onChange}
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
